import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { OAuthProviderButtons } from "./oauth-provider-buttons"
import type { OAuthProvider } from "@/lib/oauth-providers"
import { Link } from "@tanstack/react-router"
import { useQuery } from "@connectrpc/connect-query"
import { create } from "@bufbuild/protobuf"
import { getPublicSignupEnabled } from "@/gen/cortado/v1/cortado-CortadoService_connectquery"
import { GetPublicSignupEnabledRequestSchema } from "@/gen/cortado/v1/cortado_pb"

export type LoginMethod = OAuthProvider | 'email'

interface LoginFormProps extends React.ComponentProps<"form"> {
  onOAuthClick?: (provider: OAuthProvider) => void
  loading?: boolean
  lastUsedMethod?: LoginMethod | null
  title?: string
  description?: string
  submitLabel?: string
  loadingLabel?: string
  fieldIdPrefix?: string
  showAccountLinks?: boolean
}

export function LoginForm({
  className,
  onOAuthClick,
  loading = false,
  lastUsedMethod,
  title = 'Login to your account',
  description = 'Enter your email below to login to your account',
  submitLabel = 'Login',
  loadingLabel = 'Logging in...',
  fieldIdPrefix = '',
  showAccountLinks = true,
  ...props
}: LoginFormProps) {
  const emailId = `${fieldIdPrefix}email`
  const passwordId = `${fieldIdPrefix}password`

  return (
    <form className={cn("flex flex-col gap-6", className)} {...props}>
      <div className="flex flex-col gap-2 text-center">
        <h1 className="display-heading text-2xl font-semibold tracking-[-0.03em] text-balance">{title}</h1>
        <p className="text-muted-foreground text-sm text-balance">
          {description}
        </p>
      </div>
      <div className="grid gap-6">
        <OAuthProviderButtons
          onProviderClick={onOAuthClick}
          loading={loading}
          lastUsedMethod={lastUsedMethod}
          actionLabel="Login"
        />
        <div className="after:border-border relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t">
          <span className="bg-background text-muted-foreground relative z-10 px-2">
            Or continue with
          </span>
        </div>
        <div className={cn(
          "grid gap-4 relative",
          lastUsedMethod === 'email' && "p-4 -mx-4 rounded-lg ring-2 ring-primary ring-offset-2"
        )}>
          {lastUsedMethod === 'email' && (
            <span className="absolute -top-2 left-3 bg-background px-1.5 text-xs text-muted-foreground z-10">
              Last used
            </span>
          )}
          <div className="grid gap-2">
            <Label htmlFor={emailId} className="font-financial text-xs uppercase tracking-[0.12em] text-dim">Email</Label>
            <Input id={emailId} name="email" type="email" placeholder="m@example.com" autoComplete="email" required tabIndex={1} />
          </div>
          <div className="grid gap-2">
            <div className="flex items-center">
              <Label htmlFor={passwordId} className="font-financial text-xs uppercase tracking-[0.12em] text-dim">Password</Label>
              {showAccountLinks && (
                <Link
                  to="/forgot-password"
                  className="ml-auto text-sm underline-offset-4 hover:underline"
                  tabIndex={3}
                >
                  Forgot your password?
                </Link>
              )}
            </div>
            <Input id={passwordId} name="password" type="password" autoComplete="current-password" required tabIndex={2} />
          </div>
          <Button type="submit" className="w-full" disabled={loading} tabIndex={4}>
            {loading ? loadingLabel : submitLabel}
          </Button>
        </div>
      </div>
      {showAccountLinks && <LoginAccountLinks />}
    </form>
  )
}

function LoginAccountLinks() {
  const signupEnabledQuery = useQuery(
    getPublicSignupEnabled,
    create(GetPublicSignupEnabledRequestSchema, {})
  )
  const showSignupLink = signupEnabledQuery.data?.enabled === true

  return (
    <>
      <div className="text-center text-sm text-muted-foreground">
        <Link to="/magic-link" className="hover:text-foreground underline-offset-4 hover:underline">
          Sign in with magic link instead
        </Link>
      </div>
      {showSignupLink && (
        <div className="text-center text-sm">
          Don&apos;t have an account?{' '}
          <Link to="/signup" className="underline underline-offset-4">
            Sign up
          </Link>
        </div>
      )}
    </>
  )
}
